import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Button, TextField, Select, RadioGroup, Spinner, activeDepartments } from '@glplus/glpweb-dsl';
import {
  vendorApiClient,
  type AttachmentAnswer,
  type MissingField,
  type ProductBlock,
  type SizeUnit,
  type SupplyInput,
} from '../../lib/vendorApiClient';
import { SIZE_UNITS, parseSizeValue } from '../../lib/addProductForm';
import { notifyProductsChanged } from '../../lib/productEvents';
import { GREEN_INK, HEADING, MUTED, RED, categoryLabel, departmentLabel, describeProductError } from './parts';

/**
 * What we still need to know about a product (§15jj). It is hidden until the
 * vendor says — nobody else can — so there is no "sort it out for me" here.
 * Any part can be answered on its own; the product goes live with the last one.
 */

const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

type Choice = '' | 'upload' | 'none';

export interface SupplyResult {
  blockState: ProductBlock['state'] | null;
  remaining: MissingField[];
}

export default function MissingInfoForm({
  vendorId,
  vendorProductId,
  missing,
  canResolve,
  onDone,
}: {
  vendorId: string;
  vendorProductId: string;
  missing: MissingField[];
  canResolve: boolean;
  onDone: (result: SupplyResult) => void;
}) {
  const { t, i18n } = useTranslation();
  const [sizeValue, setSizeValue] = useState('');
  const [sizeUnit, setSizeUnit] = useState('');
  const [department, setDepartment] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [ingredients, setIngredients] = useState('');
  const [photo, setPhoto] = useState<{ choice: Choice; url: string | null }>({ choice: '', url: null });
  const [label, setLabel] = useState<{ choice: Choice; url: string | null }>({ choice: '', url: null });
  const [uploading, setUploading] = useState<'photo' | 'label' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A message about what was wrong is stale the moment anything is changed.
  useEffect(() => setError(null), [sizeValue, sizeUnit, department, category, description, ingredients, photo, label]);

  const departments = useMemo(
    () =>
      activeDepartments
        .map((d) => ({ value: d.label, label: departmentLabel(t, d.label), categories: d.categories, last: d.key === 'other' }))
        .sort((a, b) => Number(a.last) - Number(b.last) || a.label.localeCompare(b.label, i18n.language)),
    [t, i18n.language],
  );
  const categories = useMemo(
    () => (departments.find((d) => d.value === department)?.categories ?? []).map((c) => ({ value: c.label, label: categoryLabel(i18n.language, department, c.label) ?? c.label })),
    [departments, department, i18n.language],
  );

  if (!canResolve) {
    return <Typography sx={{ mt: 1.5, fontSize: 14, color: MUTED }}>{t('products.questions.ownerAnswers')}</Typography>;
  }

  const has = (field: MissingField) => missing.includes(field);
  const id = vendorProductId;

  const upload = async (which: 'photo' | 'label', file: File) => {
    if (file.size > MAX_UPLOAD_BYTES) return setError(t('products.errors.imageTooLarge'));
    setUploading(which);
    setError(null);
    try {
      const { cdnUrl } = await vendorApiClient.uploadAsset(vendorId, which === 'photo' ? 'product-photo' : 'label-photo', file);
      (which === 'photo' ? setPhoto : setLabel)({ choice: 'upload', url: cdnUrl });
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setUploading(null);
    }
  };

  const attachment = (a: { choice: Choice; url: string | null }): AttachmentAnswer | undefined =>
    a.choice === 'none' ? { kind: 'none' } : a.choice === 'upload' && a.url ? { kind: 'upload', url: a.url } : undefined;

  /** Only what has been filled in is sent; a half-filled part is pointed out rather than dropped. */
  const build = (): { input: SupplyInput } | { problem: string } => {
    const input: SupplyInput = {};
    if (has('size') && (sizeValue.trim() || sizeUnit)) {
      const amount = parseSizeValue(sizeValue);
      if (amount === null || !sizeUnit) return { problem: t('products.missing.errors.size') };
      input.size = { sizeValue: amount, sizeUnit: sizeUnit as SizeUnit };
    }
    if (has('department_category') && (department || category)) {
      if (!department || !category) return { problem: t('products.errors.category_required') };
      input.department = department;
      input.category = category;
    }
    if (has('description_short') && description.trim()) input.descriptionShort = description.trim();
    if (has('ingredients') && ingredients.trim()) input.ingredients = ingredients.trim();
    if (has('photo')) {
      if (photo.choice === 'upload' && !photo.url) return { problem: t('products.errors.photo_missing') };
      const a = attachment(photo);
      if (a) input.photo = a;
    }
    if (has('nutrition_label')) {
      if (label.choice === 'upload' && !label.url) return { problem: t('products.errors.label_missing') };
      const a = attachment(label);
      if (a) input.nutritionLabel = a;
    }
    if (Object.keys(input).length === 0) return { problem: t('products.missing.errors.nothing') };
    return { input };
  };

  const save = async () => {
    const built = build();
    if ('problem' in built) return setError(built.problem);
    setBusy(true);
    setError(null);
    try {
      const result = await vendorApiClient.supplyMissing(vendorId, vendorProductId, built.input);
      notifyProductsChanged();
      onDone({ blockState: result.blockState, remaining: result.remaining });
    } catch (e) {
      setError(describeProductError(e, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid={`missing-${id}`}>
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {has('size') && (
          <Part title={t('products.missing.size.title')} hint={t('products.missing.size.hint')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '140px 200px' }, gap: 1.5, alignItems: 'end' }}>
              <TextField
                id={`missing-size-${id}`}
                ariaLabel={t('products.missing.size.amount')}
                label={t('products.missing.size.amount')}
                value={sizeValue}
                onChange={(e) => setSizeValue(e.target.value)}
                disabled={busy}
                fullWidth
                slotProps={{ htmlInput: { inputMode: 'decimal', maxLength: 12 } }}
                dataTestId={`missing-size-value-${id}`}
              />
              <Box data-testid={`missing-size-unit-${id}`}>
                <Select
                  ariaLabel={t('products.missing.size.unit')}
                  placeholder={t('products.missing.size.unit')}
                  value={sizeUnit}
                  onChange={(_e, v) => setSizeUnit(v)}
                  options={SIZE_UNITS.map((u) => ({ value: u, label: t(`products.add.form.units.${u.replace(' ', '_')}`) }))}
                  disabled={busy}
                  size="small"
                  fullWidth
                />
              </Box>
            </Box>
          </Part>
        )}

        {has('department_category') && (
          <Part title={t('products.missing.department.title')} hint={t('products.missing.department.hint')}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <Box data-testid={`missing-department-${id}`}>
                <Select
                  ariaLabel={t('products.department')}
                  placeholder={t('products.department')}
                  value={department}
                  onChange={(_e, v) => {
                    setDepartment(v);
                    setCategory('');
                  }}
                  options={departments.map((d) => ({ value: d.value, label: d.label }))}
                  disabled={busy}
                  size="small"
                  fullWidth
                />
              </Box>
              <Box data-testid={`missing-category-${id}`}>
                <Select
                  ariaLabel={t('products.add.form.category')}
                  placeholder={t('products.add.form.category')}
                  value={category}
                  onChange={(_e, v) => setCategory(v)}
                  options={categories}
                  disabled={busy || !department}
                  size="small"
                  fullWidth
                />
              </Box>
            </Box>
          </Part>
        )}

        {has('description_short') && (
          <Part title={t('products.missing.description.title')} hint={t('products.missing.description.hint')}>
            <TextField
              id={`missing-description-${id}`}
              ariaLabel={t('products.request.field.description_short')}
              label={t('products.request.field.description_short')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
              fullWidth
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              dataTestId={`missing-description-input-${id}`}
            />
          </Part>
        )}

        {has('ingredients') && (
          <Part title={t('products.missing.ingredients.title')} hint={t('products.missing.ingredients.hint')}>
            <TextField
              id={`missing-ingredients-${id}`}
              ariaLabel={t('products.request.field.ingredients')}
              label={t('products.request.field.ingredients')}
              value={ingredients}
              onChange={(e) => setIngredients(e.target.value)}
              disabled={busy}
              fullWidth
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 5000 } }}
              dataTestId={`missing-ingredients-input-${id}`}
            />
          </Part>
        )}

        {has('photo') && (
          <AttachmentPart
            which="photo"
            id={id}
            state={photo}
            uploading={uploading === 'photo'}
            disabled={busy || uploading !== null}
            onChoice={(choice) => setPhoto((p) => ({ ...p, choice }))}
            onFile={(f) => void upload('photo', f)}
          />
        )}

        {has('nutrition_label') && (
          <AttachmentPart
            which="nutritionLabel"
            id={id}
            state={label}
            uploading={uploading === 'label'}
            disabled={busy || uploading !== null}
            onChoice={(choice) => setLabel((p) => ({ ...p, choice }))}
            onFile={(f) => void upload('label', f)}
          />
        )}
      </Box>

      {error && (
        <Typography role="alert" sx={{ mt: 1.5, fontSize: 14, fontWeight: 700, color: RED }} data-testid={`missing-error-${id}`}>
          {error}
        </Typography>
      )}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography sx={{ flex: 1, minWidth: 200, fontSize: 13, color: MUTED }}>{t('products.missing.partial')}</Typography>
        <Button
          ariaLabel={t('products.missing.save')}
          buttonText={t('products.missing.save')}
          variant="contained"
          color="primary"
          disabled={busy || uploading !== null}
          onClick={() => void save()}
          dataTestId={`missing-save-${id}`}
        />
      </Box>
    </Box>
  );
}

function Part({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <Box sx={{ p: 2, bgcolor: '#F7F7F8', borderRadius: '8px' }}>
      <Typography component="h3" sx={{ fontSize: 15, fontWeight: 700, color: HEADING }}>
        {title}
      </Typography>
      <Typography sx={{ mt: 0.25, mb: 1, fontSize: 13, color: MUTED }}>{hint}</Typography>
      {children}
    </Box>
  );
}

/** "Upload one" or "I don't have one" — both are answers; leaving it blank is not (§15c). */
function AttachmentPart({
  which,
  id,
  state,
  uploading,
  disabled,
  onChoice,
  onFile,
}: {
  which: 'photo' | 'nutritionLabel';
  id: string;
  state: { choice: Choice; url: string | null };
  uploading: boolean;
  disabled: boolean;
  onChoice: (choice: Choice) => void;
  onFile: (file: File) => void;
}) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement>(null);
  const base = `products.add.form.${which}`;
  return (
    <Part title={t(`products.missing.${which}.title`)} hint={t(`products.missing.${which}.hint`)}>
      <Box data-testid={`missing-${which}-${id}`}>
        <RadioGroup
          ariaLabel={t(`${base}.label`)}
          name={`missing-${which}-${id}`}
          row
          value={state.choice}
          onChange={(e) => onChoice(e.target.value as Choice)}
          options={[
            { value: 'upload', label: t(`${base}.upload`), disabled },
            { value: 'none', label: t(`${base}.none`), disabled },
          ]}
        />
        {state.choice === 'none' && <Typography sx={{ fontSize: 13, color: GREEN_INK }}>{t(`${base}.noneHint`)}</Typography>}
        {state.choice === 'upload' && (
          <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff', border: `1px dashed #9AA3AD`, borderRadius: '8px', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {state.url && <Box component="img" src={state.url} alt={t('products.request.attach.previewAlt')} sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: '8px' }} />}
            <Typography sx={{ flex: 1, minWidth: 160, fontSize: 13, color: MUTED }}>{t(state.url ? `${base}.uploaded` : 'products.request.attach.hint')}</Typography>
            {uploading ? (
              <Spinner color="brand" />
            ) : (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  data-testid={`missing-${which}-file-${id}`}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) onFile(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  ariaLabel={t(state.url ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                  buttonText={t(state.url ? 'products.request.attach.replace' : 'products.request.attach.choose')}
                  variant="outlinedGreen"
                  disabled={disabled}
                  onClick={() => fileInput.current?.click()}
                />
              </>
            )}
          </Box>
        )}
      </Box>
    </Part>
  );
}
